import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Calculator, Mail } from "lucide-react";
import { buildEnquiryText, openWhatsApp, whatsappUrl, mailtoUrl, trackWhatsApp } from "../lib/enquiryShare";
import { WhatsAppIcon } from "./WhatsAppButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { products, quoteUnits, printProcesses, substrates } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  product: "",
  quantity: "",
  unit: "kg",
  shade: "",
  print_process: "",
  substrate: "",
  name: "",
  email: "",
  phone: "",
  company: "",
  message: "",
};

const validate = (v) => {
  const e = {};
  if (!v.product) e.product = "Please select a product";
  if (!v.quantity || Number(v.quantity) <= 0) e.quantity = "Enter the quantity required";
  if (!v.name.trim() || v.name.trim().length < 2) e.name = "Please enter your name";
  if (!/^\S+@\S+\.\S+$/.test(v.email)) e.email = "Please enter a valid email";
  if (!/^[0-9+\-\s]{10,15}$/.test(v.phone)) e.phone = "Please enter a valid mobile number";
  return e;
};

const Field = ({ label, error, children, required }) => (
  <div>
    <Label className="text-[13px] font-bold text-[#4d4d4f] mb-1.5 block">
      {label} {required && <span className="text-[#c0392b]">*</span>}
    </Label>
    {children}
    {error && <p className="text-[#c0392b] text-[12px] mt-1">{error}</p>}
  </div>
);

const QuoteForm = ({ presetProduct = "", presetShade = "" }) => {
  const [values, setValues] = useState({ ...initial, product: presetProduct, shade: presetShade });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    setValues((v) => ({
      ...v,
      product: presetProduct || v.product,
      shade: presetShade || v.shade,
    }));
  }, [presetProduct, presetShade]);

  const set = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };
  const onChange = (e) => set(e.target.name, e.target.value);

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, { ...values, enquiry_type: "quote", source: "quote" });
      const text = buildEnquiryText(values, "quote");
      const meta = { name: values.name, phone: values.phone, email: values.email };
      setDone({ product: values.product, quantity: values.quantity, unit: values.unit, text, meta });
      setValues({ ...initial });
      const opened = openWhatsApp(text, "quote-form", meta);
      toast.success("Quote request received", {
        description: opened
          ? "WhatsApp has opened with your request - tap Send to reach our team instantly."
          : "Use the WhatsApp button below to reach our team instantly.",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg?.replace(/^Value error, /, "")).join(". ")
        : typeof detail === "string" ? detail : "Something went wrong. Please try again or call us directly.";
      toast.error("Could not send your quote request", { description: msg });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="quote-success">
        <CheckCircle2 size={48} className="mx-auto mb-4 text-[#7ab929]" />
        <h3 className="text-[22px] font-bold text-[#231f20]">Quote Request Received</h3>
        <p className="mt-2 text-[15px] text-[#58595b]">
          Thank you. Our team will price <strong>{done.quantity} {done.unit}</strong> of <strong>{done.product}</strong> and reply within one working day.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <a href={whatsappUrl(done.text)} target="_blank" rel="noreferrer" className="btn-whatsapp" onClick={() => trackWhatsApp(done.text, "quote-success-button", done.meta)} data-testid="quote-share-whatsapp">
            <WhatsAppIcon size={18} /> Send on WhatsApp
          </a>
          <a href={mailtoUrl(done.text, "quote")} className="btn-outline-dark" data-testid="quote-share-email">
            <Mail size={16} /> Send by Email
          </a>
        </div>
        <button type="button" onClick={() => setDone(null)} className="mt-6 text-[13px] underline underline-offset-4 text-[#6d6e71] hover:text-[#231f20]" data-testid="quote-send-another">
          Request another quote
        </button>
      </div>
    );
  }

  const inputCls = "field field--light";

  return (
    <form onSubmit={onSubmit} noValidate data-testid="quote-form">
      <div className="flex items-center gap-2 mb-5 text-[#231f20]">
        <Calculator size={18} className="text-[#7ab929]" />
        <h4 className="font-bold text-[16px]">Requirement details</h4>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Product" required error={errors.product}>
          <Select value={values.product} onValueChange={(v) => set("product", v)}>
            <SelectTrigger className="h-[46px] bg-white border-[#d9d9d9] text-[15px]" data-testid="quote-product">
              <SelectValue placeholder="Select a product" />
            </SelectTrigger>
            <SelectContent>
              {products.map((p) => (
                <SelectItem key={p.id} value={p.title} data-testid={`quote-product-${p.id}`}>{p.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <div className="grid grid-cols-[1fr_150px] gap-3">
          <Field label="Quantity" required error={errors.quantity}>
            <input name="quantity" type="number" min="1" value={values.quantity} onChange={onChange} placeholder="e.g. 200" className={inputCls} data-testid="quote-quantity" />
          </Field>
          <Field label="Unit">
            <Select value={values.unit} onValueChange={(v) => set("unit", v)}>
              <SelectTrigger className="h-[46px] bg-white border-[#d9d9d9] text-[15px]" data-testid="quote-unit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {quoteUnits.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field label="Shade / Pantone reference">
          <input name="shade" value={values.shade} onChange={onChange} placeholder="e.g. Pantone 485 C, Process Cyan, Brand Red" className={inputCls} data-testid="quote-shade" />
        </Field>

        <Field label="Print process">
          <Select value={values.print_process} onValueChange={(v) => set("print_process", v)}>
            <SelectTrigger className="h-[46px] bg-white border-[#d9d9d9] text-[15px]" data-testid="quote-process">
              <SelectValue placeholder="Select print process" />
            </SelectTrigger>
            <SelectContent>
              {printProcesses.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Substrate">
          <Select value={values.substrate} onValueChange={(v) => set("substrate", v)}>
            <SelectTrigger className="h-[46px] bg-white border-[#d9d9d9] text-[15px]" data-testid="quote-substrate">
              <SelectValue placeholder="Select substrate" />
            </SelectTrigger>
            <SelectContent>
              {substrates.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Company">
          <input name="company" value={values.company} onChange={onChange} placeholder="Company name (optional)" className={inputCls} data-testid="quote-company" />
        </Field>
      </div>

      <div className="flex items-center gap-2 mt-8 mb-5 text-[#231f20]">
        <h4 className="font-bold text-[16px]">Your contact details</h4>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        <Field label="Name" required error={errors.name}>
          <input name="name" value={values.name} onChange={onChange} placeholder="Your name" className={inputCls} data-testid="quote-name" />
        </Field>
        <Field label="Email" required error={errors.email}>
          <input name="email" type="email" value={values.email} onChange={onChange} placeholder="you@company.com" className={inputCls} data-testid="quote-email" />
        </Field>
        <Field label="Mobile No." required error={errors.phone}>
          <input name="phone" value={values.phone} onChange={onChange} placeholder="10-digit mobile" className={inputCls} data-testid="quote-phone" />
        </Field>
      </div>
      <div className="mt-5">
        <Field label="Additional notes">
          <textarea name="message" rows={3} value={values.message} onChange={onChange} placeholder="Delivery timeline, packing preference, end use, etc." className={`${inputCls} resize-none`} data-testid="quote-message" />
        </Field>
      </div>

      <div className="mt-8">
        <button type="submit" disabled={loading} className="btn-green min-w-[220px] justify-center" data-testid="quote-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : null}
          {loading ? "Sending..." : "Request Quote"}
        </button>
      </div>
    </form>
  );
};

export default QuoteForm;
